import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import {
  Search,
  MapPin,
  ArrowRight,
  Sparkles,
  Users,
  Shield,
  Wand2,
  Heart,
  BadgeCheck,
  Database,
  
  HandHeart,
  Building2,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { ParallaxHero } from "@/components/site/ParallaxHero";
import { heroImages } from "@/lib/hero-images";



import {
  featuredEducationUnitsQueryOptions,
  unitPath,
  type EducationUnit,
} from "@/lib/education-units";
import { SchoolSuggestions } from "@/components/site/SchoolSuggestions";
import { FeaturedSchoolCard } from "@/components/site/FeaturedSchoolCard";
import { SchoolCardGrid } from "@/components/site/SchoolCardGrid";
import { FeaturedSchoolSkeleton, FeaturedSchoolsEmpty } from "@/components/site/FeaturedSchoolSkeleton";
import { CacheStatusBadge } from "@/components/site/CacheStatusBadge";
import { Section } from "@/components/site/Section";
import { SavedComparisonsSection } from "@/components/site/SavedComparisons";
import { MeritCalculator } from "@/components/site/MeritCalculator";
import { SchoolMatchWidget } from "@/components/site/SchoolMatchWidget";
import { Reveal } from "@/components/site/Reveal";
import { WordReveal } from "@/components/site/WordReveal";
import { RotatingPlaceholder } from "@/components/site/RotatingPlaceholder";
import {
  PlatformStatsSequence,
} from "@/components/site/HomeStage2Sequences";



import { Button as DsButton } from "@/components/ds/Button";
import { GronYta } from "@/components/ds/Surface";
import {
  antal,
  coverageStatsQueryOptions,
  formatDatum,
  kommunAntal,
  skolformer,
  useCoverageStats,
  type CoverageStats,
} from "@/lib/coverage-stats";


export const FEATURED_HOME_LIMIT = 6;

export const Route = createFileRoute("/")({
  // Vänta in datan i loadern: "Utvalda skolor" ska finnas i serverrenderad
  // HTML (skolnamn + länkar är sidans viktigaste interna länkblock och
  // crawlers som inte kör JS såg tidigare bara "Laddar utvalda skolor…").
  loader: async ({ context }) => {
    // Returnera datan också: den används som initialData på klienten så att
    // hydreringen matchar serverns HTML (annars renderade klienten skeleton).
    // Datahämtningen får aldrig fälla hela startsidan: vid ett tillfälligt
    // fel renderas sidan utan utvalda skolor och klienten hämtar om.
    const [featured] = await Promise.all([
      context.queryClient
        .ensureQueryData(featuredEducationUnitsQueryOptions(FEATURED_HOME_LIMIT))
        .catch((error: unknown) => {
          console.error("[home] kunde inte hämta utvalda skolor", error);
          return undefined;
        }),
      // Täckningssiffrorna renderas i hero, footer och statistiksektionen —
      // de måste finnas i serverns HTML för att undvika hydration mismatch.
      context.queryClient.ensureQueryData(coverageStatsQueryOptions()).catch((error: unknown) => {
        console.error("[home] kunde inte hämta täckningssiffror", error);
        return undefined;
      }),
    ]);
    return { featured };

  },
  head: () => {
    const title = "Skolvyn — Hitta, jämför och kontakta Sveriges skolor";
    const description =
      "Sök, jämför och kontakta skolor i hela Sverige. Öppen data från Skolverket, verifierade profiler och skolinformation du kan lita på.";
    const url = "https://skolvyn.se/";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        // Open Graph
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        // Twitter Card — samma titel, beskrivning och bild som OG
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        // Parallax-heron är sidans LCP. Preload av AVIF-varianten låter
        // webbläsaren hämta landskapet parallellt med CSS/JS i stället för
        // att vänta tills <picture> upptäcks efter hydrering.
        {
          rel: "preload",
          as: "image",
          href: heroImages.parallaxLandskap.src,
          imageSrcSet: heroImages.parallaxLandskap.avif,
          imageSizes: "100vw",
          type: "image/avif",
          fetchPriority: "high",
        },
        // Eleven i förgrunden ligger ovanpå, lägre prioritet så den inte
        // konkurrerar med landskapet om bandbredden.
        {
          rel: "preload",
          as: "image",
          href: heroImages.parallaxPojke.src,
          imageSrcSet: heroImages.parallaxPojke.avif,
          imageSizes: "(min-width: 768px) 400px, 60vw",
          type: "image/avif",
          fetchPriority: "low",
        },
      ],

      // WebSite/Organization ligger i rot-routens @graph — dupliceras inte här.
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: HOME_FAQ.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
      ],
    };
  },
  component: Home,
  errorComponent: ({ error }) => (
    <div role="alert" className="mx-auto max-w-3xl px-5 py-section text-center">
      <p className="text-ink-muted">Kunde inte ladda startsidan: {error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-section text-center">
      <p className="text-ink-muted">Sidan hittades inte.</p>
    </div>
  ),
});

const quickLinks: Array<{
  label: string;
  kommun?: string;
  search?: Record<string, string>;
}> = [
  { label: "Skolor i Malmö", kommun: "malmo" },
  { label: "Skolor i Stockholm", kommun: "stockholm" },
  { label: "Montessori", search: { q: "Montessori" } },
  { label: "Skolor i Lund", kommun: "lund" },
];


export const HOME_FAQ: Array<{ q: string; a: string }> = [
  {
    q: "Kostar det något att använda Skolvyn?",
    a: "Nej. Att söka, jämföra, spara favoriter och skicka intresseanmälan är helt gratis för familjer, och du behöver inget konto för att komma igång.",
  },
  {
    q: "Var kommer informationen om skolorna ifrån?",
    a: "Grunduppgifterna hämtas från Skolverkets nationella skolenhetsregister. Skolor som gjort anspråk på sin profil kompletterar själva med bilder, beskrivning, personal och kontaktuppgifter.",
  },
  {
    q: "Rankar ni skolorna?",
    a: "Nej. Vi visar öppen data sida vid sida utan sammanvägd poäng eller topplista. Att en skola betalar för en utökad profil påverkar aldrig ordningen i sökresultaten.",
  },
  {
    q: "Hur jämför jag flera skolor?",
    a: "Markera upp till tre skolor i sökresultatet så ställs de upp bredvid varandra med skolform, årskurser, huvudman, läge och det skolan själv har lagt in.",
  },
  {
    q: "Kan jag hitta skolor med bra stöd för mitt barns behov?",
    a: "Ja. I sökningen och på varje profil finns information om elevhälsa, anpassningar och stöd, och du kan svara på några frågor i Hitta rätt skola för att få förslag som utgår från era behov.",
  },
];

const guideLinks: Array<{ to: string; title: string; text: string; cta: string }> = [
  {
    to: "/guider/skolvalet",
    title: "Skolvalet steg för steg",
    text: "Antagning och kö, stödtrappan, vägen till en plats och frågorna att ställa vid besöket.",
    cta: "Läs guiden",
  },
  {
    to: "/guider/gymnasieprogram",
    title: "Alla gymnasieprogram",
    text: "18 nationella program med inriktningar, yrken och vidare studier.",
    cta: "Se alla 18 program",
  },
  {
    to: "/verktyg/meritvarde",
    title: "Räkna ut ditt meritvärde",
    text: "Fyll i dina betyg och se meritvärdet för gymnasiet direkt.",
    cta: "Öppna meritvärdesräknaren",
  },
  {
    to: "/hitta-skola",
    title: "Hitta rätt skola",
    text: "Svara på några frågor och få skolor som matchar era behov.",
    cta: "Starta matchningen",
  },
  {
    to: "/syv",
    title: "Verktyg för vägledare",
    text: "Jämförelseformulär och mallar att dela med elever och vårdnadshavare.",
    cta: "Öppna vägledarverktygen",
  },
  {
    to: "/nyheter",
    title: "Nyheter och guider",
    text: "Skolval, antagning och vardagsråd — uppdaterat löpande.",
    cta: "Läs de senaste guiderna",
  },
];


// Sorterade efter kommunens ungefärliga storlek, inte efter antal skolor.
const popularKommuner = [
  { name: "Stockholm", slug: "stockholm" },
  { name: "Göteborg", slug: "goteborg" },
  { name: "Malmö", slug: "malmo" },
  { name: "Uppsala", slug: "uppsala" },
  { name: "Linköping", slug: "linkoping" },
  { name: "Västerås", slug: "vasteras" },
  { name: "Örebro", slug: "orebro" },
  { name: "Helsingborg", slug: "helsingborg" },
  { name: "Norrköping", slug: "norrkoping" },
  { name: "Jönköping", slug: "jonkoping" },
  { name: "Umeå", slug: "umea" },
  { name: "Lund", slug: "lund" },
  { name: "Borås", slug: "boras" },
  { name: "Huddinge", slug: "huddinge" },
  { name: "Eskilstuna", slug: "eskilstuna" },
  { name: "Nacka", slug: "nacka" },
  { name: "Gävle", slug: "gavle" },
  { name: "Södertälje", slug: "sodertalje" },
  { name: "Sundsvall", slug: "sundsvall" },
  { name: "Halmstad", slug: "halmstad" },
];

function Home() {
  return (
    <>
      <ParallaxHero search={<HeroSearch />} />
      <Hero />

      <PlatformStatsSequence />
      <HowItWorks />
      <MatchTeaser />
      <MeritTeaser />
      <FeaturedSchools />
      <Section>
        <SavedComparisonsSection />
      </Section>
      <PopularKommuner />
      <GuidesStrip />
      <WhyPillars />
      <HomeFaq />
      <ForSchoolsSplit />
      <FinalCTA />
    </>
  );
}


function MatchTeaser() {
  return (
    <section className="mx-auto mt-14 max-w-6xl px-5 lg:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <p className="eyebrow inline-flex items-center gap-2">
          <Wand2 className="h-3.5 w-3.5 text-primary" aria-hidden /> Personliga rekommendationer
        </p>
        <h2 className="mt-3 font-display text-h1 leading-tight sm:text-h1">
          Vilken skola passar <span className="underline-accent">ditt barn</span>?
        </h2>
        <p className="mt-4 text-ink-muted">
          Svara på några frågor om läge, behov och vad ni värdesätter — så visar vi skolorna som
          matchar. Tar ungefär två minuter.
        </p>
        <ul className="mono mt-4 flex flex-wrap justify-center gap-x-3 gap-y-1 text-label text-ink-muted">
          <li>Läge och restid</li>
          <li aria-hidden>·</li>
          <li>Vad familjen värdesätter</li>
          <li aria-hidden>·</li>
          <li>Stöd och anpassningar (frivilligt)</li>
          <li aria-hidden>·</li>
          <li>Pedagogik och profil</li>
        </ul>
        <p className="mt-4 flex items-start justify-center gap-2 text-small text-ink-muted">
          <Shield className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
          <span>
            Dina svar används bara för att ta fram rekommendationer och delas inte med skolorna.
          </span>
        </p>
      </div>
      <div className="mt-8">
        <SchoolMatchWidget variant="compact" />
      </div>
    </section>
  );
}

function MeritTeaser() {
  return (
    <section className="mx-auto mt-10 max-w-6xl px-5 lg:px-8">
      <MeritCalculator variant="compact" />
    </section>
  );
}



/** Antal skolor i en kommun, formaterat — null när kommunen saknas. */
function kommunLabel(stats: CoverageStats | undefined, namn: string): string | null {
  const n = antal(kommunAntal(stats, namn));
  return n ? `${n} skolor` : null;
}

function HeroSearch() {
  const navigate = useNavigate();
  const heroFormer = skolformer(useCoverageStats().data);
  const [ort, setOrt] = useState("");
  const [touched, setTouched] = useState(false);
  const [form, setForm] = useState("");
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [items, setItems] = useState<EducationUnit[]>([]);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listboxId = "hero-school-suggestions";

  // Debounce så vi inte söker på varje tangenttryck.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(ort), 180);
    return () => clearTimeout(t);
  }, [ort]);

  // Stäng listan vid klick utanför.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const showList = open && debounced.trim().length >= 2;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Enter på ett markerat förslag → direkt till skolans profil.
    if (showList && activeIndex >= 0 && activeIndex < items.length) {
      const unit = items[activeIndex]!;
      setOpen(false);
      navigate({ to: unitPath(unit) });
      return;
    }
    const params: Record<string, string> = {};
    if (ort) params.q = ort;
    if (form) params.type = form;
    setOpen(false);
    navigate({ to: "/skolor", search: params as never });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showList) return;
    const max = items.length; // sista raden = "visa alla"
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i >= max ? 0 : i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? max : i - 1));
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <>
      <form
        onSubmit={onSubmit}
        className={`relative mt-8 rounded-card border border-border bg-surface/95 p-2.5 shadow-1 backdrop-blur ${
          showList ? "z-50" : "z-10"
        }`}
        aria-label="Sök skola"
      >
        <div className="grid gap-2 p-2 md:grid-cols-[1.5fr_1fr_auto]">
          <div className="relative" ref={wrapRef}>
            <MapPin className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
            <input
              type="text"
              value={ort}
              onChange={(e) => {
                setOrt(e.target.value);
                setTouched(true);
                setOpen(true);
                setActiveIndex(-1);
              }}
              onFocus={() => {
                setTouched(true);
                setOpen(true);
              }}
              onKeyDown={onKeyDown}
              role="combobox"
              aria-expanded={showList}
              aria-controls={listboxId}
              aria-autocomplete="list"
              autoComplete="off"
              placeholder="Skolnamn, ort eller kommun"
              className={`h-14 w-full rounded-card border border-border bg-background pl-11 pr-4 text-body outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15 ${
                !touched && !ort ? "placeholder:text-transparent" : ""
              }`}
              aria-label="Skolnamn, ort eller kommun — skriv till exempel Malmö, Lund eller ett skolnamn"
            />
            <RotatingPlaceholder active={!touched && !ort} className="left-11 right-4" />
            {showList ? (
              <SchoolSuggestions
                query={debounced}
                listboxId={listboxId}
                activeIndex={activeIndex}
                onItemsChange={setItems}
                onClose={() => {
                  setOpen(false);
                  setActiveIndex(-1);
                }}
              />
            ) : null}
          </div>


          <select
            value={form}
            onChange={(e) => setForm(e.target.value)}
            className="h-14 w-full rounded-card border border-border bg-background px-4 text-body outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
            aria-label="Skolform"
          >
            <option value="">Alla skolformer</option>
            {/* Endast skolformer som faktiskt finns i registret — förskola
                ingår t.ex. inte i Skolverkets skolenhetsregister. */}
            {heroFormer.map(([namn]) => (
              <option key={namn} value={namn}>
                {namn}
              </option>
            ))}
          </select>
          <Button
            type="submit"
            className="group inline-flex h-14 items-center justify-center gap-2 rounded-card bg-primary px-6 text-body font-semibold text-primary-foreground shadow-1 transition hover:bg-primary-hover hover:shadow-1"
          >
            <Search className="h-4 w-4" /> Sök
            <ArrowRight className="h-4 w-4 transition -translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100" />
          </Button>
        </div>
      </form>

      <div
        aria-hidden={showList}
        className={`relative z-0 mt-6 flex flex-wrap items-center gap-2 transition-all duration-200 ${
          showList
            ? "pointer-events-none translate-y-1 opacity-0"
            : "translate-y-0 opacity-100"
        }`}
      >
        {quickLinks.map((q) =>
          q.kommun ? (
            <Link
              key={q.label}
              to="/skolor/$kommun"
              params={{ kommun: q.kommun }}
              className="rounded-pill border border-border bg-surface/80 px-3.5 py-1.5 text-small text-ink-muted backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/30 hover:bg-primary-soft hover:text-primary"
            >
              {q.label}
            </Link>
          ) : (
            <Link
              key={q.label}
              to="/skolor"
              search={(q.search ?? {}) as never}
              className="rounded-pill border border-border bg-surface/80 px-3.5 py-1.5 text-small text-ink-muted backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/30 hover:bg-primary-soft hover:text-primary"
            >
              {q.label}
            </Link>
          ),
        )}
      </div>
    </>
  );
}

function Hero() {
  const { data: coverage } = useCoverageStats();
  const heroEnheter = antal(coverage?.skolenheter_totalt);
  const heroKommuner = antal(coverage?.kommuner_med_skolor);
  const heroDatum = formatDatum(coverage?.senast_uppdaterad);
  return (
    <section className="relative overflow-hidden topo-bg">
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
        <div className="absolute -left-24 top-16 h-72 w-72 rounded-pill bg-primary-soft/70 blur-3xl" />
        <div className="absolute right-[-6rem] top-40 h-80 w-80 rounded-pill bg-accent-soft/60 blur-3xl" />
      </div>
      <div className="mx-auto max-w-7xl px-5 pb-12 pt-10 lg:px-8 lg:pb-16 lg:pt-14">
        <div className="max-w-4xl">
          <div>
            <p className="eyebrow animate-fade-up-sm inline-flex items-center gap-2 rounded-pill border border-border bg-surface/70 px-3 py-1.5 backdrop-blur">
              <Heart className="h-3.5 w-3.5 text-primary" aria-hidden /> För familjer som väljer med hjärtat
            </p>
            {/* Effekt 2 — ordvis reveal. Rubriken finns i slutläge i serverns
                HTML; rörelsen läggs på först efter mount. */}
            <WordReveal
              as="h2"
              className="mt-6 display text-display"
              segments={[
                { text: "Hitta," },
                { text: "jämför", className: "underline-accent" },
                { text: "och kontakta Sveriges skolor" },
              ]}
              delayStart={80}
            />
            <p
              className="mt-6 max-w-xl text-h4 text-ink-muted animate-fade-up sm:text-h3"
              style={{ animationDelay: "160ms" }}
            >
              Hitta rätt skola — en plats där ditt barn får vara sig själv. Se vardagen bakom skolan: bilder, ord från lärarna och röster från andra föräldrar, så att magkänslan får något att vila på.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2">
              {heroEnheter ? (
                <>
                  <span className="mono text-small text-ink-muted">{heroEnheter} skolor</span>
                  <span className="h-1 w-1 rounded-pill bg-border" aria-hidden />
                </>
              ) : null}
              {heroKommuner ? (
                <>
                  <span className="mono text-small text-ink-muted">{heroKommuner} kommuner</span>
                  <span className="h-1 w-1 rounded-pill bg-border" aria-hidden />
                </>
              ) : null}
              <span className="mono text-small text-ink-muted">
                Data från Skolverket{heroDatum ? `, uppdaterat ${heroDatum}` : ""}
              </span>
              <span className="h-1 w-1 rounded-pill bg-border" aria-hidden />
              <span className="mono text-small text-ink-muted">Gratis, utan konto</span>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Search, title: "Sök", text: "Filtrera på ort, skolform och pedagogik. Se pins på kartan direkt." },
    { icon: Sparkles, title: "Jämför", text: "Ställ upp till tre skolor sida vid sida. Transparent data — ingen poängrankning." },
    { icon: ArrowRight, title: "Kontakta", text: "Skicka intresseanmälan eller ansökan direkt från skolans profil." },
  ];
  return (
    <Section>
      <div className="max-w-2xl">
        <p className="eyebrow">Så funkar det</p>
        <h2 className="mt-3 text-h1 md:text-display">Tre steg — utan konto och utan algoritm</h2>
        <p className="mt-4 max-w-xl text-ink-muted">Vi visar allt vi har och är tydliga med vad som saknas. Ni bestämmer resten.</p>
      </div>
      <div className="relative mt-14 grid gap-6 md:grid-cols-3">
        <div className="pointer-events-none absolute left-8 right-8 top-16 hidden h-px bg-gradient-to-r from-transparent via-border to-transparent md:block" aria-hidden />
        {steps.map((s, i) => (
          <div key={s.title} className="group relative rounded-card border border-border bg-surface p-card transition hover:-translate-y-1 hover:border-primary/25 hover:shadow-1">
            <div className="flex items-center justify-between">
              <span className="grid h-12 w-12 place-items-center rounded-card bg-primary-soft text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
                <s.icon className="h-5 w-5" />
              </span>
              <span className="mono text-small text-ink-muted">0{i + 1}</span>
            </div>
            <h3 className="mt-6 font-display text-h2">{s.title}</h3>
            <p className="mt-2 text-ink-muted">{s.text}</p>
          </div>
        ))}
      </div>
      <div className="mt-10">
        <Link
          to="/hitta-skola"
          className="inline-flex items-center gap-2 rounded-card bg-primary px-6 py-3.5 text-body font-semibold text-primary-foreground transition hover:bg-primary-hover"
        >
          Börja er resa <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </Section>

  );
}

function FeaturedSchools() {
  const { featured: initialFeatured } = Route.useLoaderData();
  const { data, isLoading, isFetching, dataUpdatedAt } = useQuery({
    ...featuredEducationUnitsQueryOptions(FEATURED_HOME_LIMIT),
    initialData: initialFeatured,
  });
  const featured = data?.items ?? [];
  return (
    <Section>
      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <div className="max-w-xl">
          <p className="eyebrow">Utvalda skolor</p>
          <h2 className="mt-3 text-h1 md:text-display">Titta in hos några skolor den här veckan</h2>
          <p className="mt-4 max-w-md text-ink-muted">Kika på vardagen — miljön, människorna och det lilla som gör en skola till en trygg plats för ditt barn.</p>
          <CacheStatusBadge
            isLoading={isLoading}
            isFetching={isFetching}
            dataUpdatedAt={dataUpdatedAt}
            className="mt-3"
          />
        </div>
        <Link to="/skolor" className="group inline-flex items-center gap-1.5 self-start text-small font-semibold text-primary hover:underline md:self-end">
          Se alla skolor <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </Link>
      </div>
      {isLoading && featured.length === 0 ? (
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: FEATURED_HOME_LIMIT }).map((_, i) => (
            <FeaturedSchoolSkeleton key={i} />
          ))}
        </div>
      ) : featured.length === 0 ? (
        <div className="mt-10">
          <FeaturedSchoolsEmpty variant="section" />
        </div>
      ) : (
        <SchoolCardGrid
          label="home:featured-grid"
          count={featured.length}
          className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {featured.map((u) => <FeaturedSchoolCard key={u.id} unit={u} />)}
        </SchoolCardGrid>

      )}
    </Section>
  );
}

function PopularKommuner() {
  const { data: coverage } = useCoverageStats();
  return (
    <section className="bg-primary-soft/25 py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-xl">
            <p className="eyebrow inline-flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-primary" /> Populära kommuner
            </p>
            <h2 className="mt-3 font-display text-h1 md:text-h1">Utforska skolor där du bor</h2>
            <p className="mt-3 text-ink-muted">
              Egna landningssidor per kommun — grupperat per skolform, med lokal statistik och direkta länkar till varje skolprofil.
            </p>
          </div>
          <Link to="/skolor" className="text-small font-semibold text-primary hover:underline">
            Se alla kommuner →
          </Link>
        </div>
        <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {popularKommuner.map((k) => (
            <li key={k.slug}>
              <Link
                to="/skolor/$kommun"
                params={{ kommun: k.slug }}
                className="group flex items-center justify-between rounded-card border border-border bg-surface px-4 py-4 shadow-1 transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-1"
              >
                <span>
                  <span className="flex items-center gap-2 font-semibold text-ink group-hover:text-primary">
                    <Building2 className="h-4 w-4 text-primary/70" />
                    {k.name}
                  </span>
                  {kommunLabel(coverage, k.name) ? (
                    <span className="mt-0.5 block text-small text-ink-muted">
                      {kommunLabel(coverage, k.name)}
                    </span>
                  ) : null}
                </span>
                <ArrowRight className="h-4 w-4 text-ink-muted transition group-hover:translate-x-0.5 group-hover:text-primary" />
              </Link>
            </li>
          ))}
        </ul>

      </div>
    </section>
  );
}

function WhyPillars() {
  const pillars = [
    {
      icon: Database,
      title: "Öppen data — allt på bordet",
      text: "Vi visar samma siffror som Skolverket publicerar. Ingen sammanvägd 'topplista', inget svartlådebetyg. Ni läser rådata och drar era egna slutsatser.",
    },
    {
      icon: BadgeCheck,
      title: "Skolorna verifieras",
      text: "Varje profil ägs av skolan själv. Innehållet uppdateras av rektorn — inte skrapat från gamla nyhetssidor eller andrahandskällor.",
    },
    {
      icon: HandHeart,
      title: "Fritt och opartiskt",
      text: "Skolvyn är gratis för familjer, och skolor betalar bara för utökade profiler. Det påverkar aldrig ordningen i sökresultaten.",
    },
  ];
  return (
    <Section>
      <div className="grid items-center gap-10 lg:grid-cols-[1fr_0.85fr]">
        <div className="max-w-2xl">
          <p className="eyebrow">Varför Skolvyn</p>
          <h2 className="mt-3 font-display text-h1 md:text-h1">Byggt för att vara transparent — inte klickvänligt</h2>
          <p className="mt-4 text-ink-muted">
            De flesta börjar sin skolsökning vid köksbordet, sent på kvällen, med en kopp kaffe och för många flikar öppna.
            Skolvyn samlar det som faktiskt behövs för beslutet på ett ställe.
          </p>
        </div>
      </div>
      <div className="mt-10 grid gap-6 md:grid-cols-3">

        {pillars.map((p) => (
          <div
            key={p.title}
            className="group relative overflow-hidden rounded-card border border-border bg-surface p-card transition hover:-translate-y-1 hover:border-primary/30 hover:shadow-1"
          >
            <span className="grid h-11 w-11 place-items-center rounded-card bg-primary-soft text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
              <p.icon className="h-5 w-5" />
            </span>
            <h3 className="mt-5 font-display text-h3 leading-snug text-ink">{p.title}</h3>
            <p className="mt-3 text-small leading-relaxed text-ink-muted">{p.text}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ForSchoolsSplit() {
  const { data: coverage } = useCoverageStats();
  const splitEnheter = antal(coverage?.skolenheter_totalt);
  const splitKommuner = antal(coverage?.kommuner_med_skolor);
  return (
    <GronYta className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-40" aria-hidden>
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-pill bg-accent/25 blur-3xl" />
        <div className="absolute right-0 bottom-0 h-80 w-80 rounded-pill bg-primary-foreground/10 blur-3xl" />
      </div>
      <div className="relative mx-auto grid max-w-7xl gap-12 px-5 py-section lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-16 lg:px-8 py-section">
        <div>
          <p className="eyebrow text-accent">För skolor</p>
          <h2 className="mt-3 text-h1 text-primary-foreground md:text-display">Driver du en skola? Ta plats där familjer och lärare letar.</h2>
          <p className="mt-5 max-w-lg text-h4 text-primary-foreground/85">
            Skolvyn är där föräldrar och elever fattar beslut. Gör anspråk på er profil — gratis — och komplettera med bilder, video och lediga tjänster.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <DsButton asChild size="stor" className="group">
              <Link to="/for-skolor">
                Se hur det fungerar <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </Link>
            </DsButton>
            <Link
              to="/for-skolor"
              className="inline-flex items-center rounded-card border border-primary-foreground/25 bg-primary-foreground/5 px-6 py-3.5 text-small font-semibold text-primary-foreground backdrop-blur transition hover:bg-primary-foreground/10"
            >
              Kontakta oss
            </Link>
          </div>
          <Link
            to="/exempel-profil"
            className="group mt-8 flex flex-col gap-2 rounded-card border border-primary-foreground/20 bg-primary-foreground/5 p-card backdrop-blur transition hover:bg-primary-foreground/10"
          >
            <p className="eyebrow text-accent">Så kan er skolas profil se ut</p>
            <p className="font-display text-h3 text-primary-foreground">
              Se hur en färdig profil ser ut
            </p>
            <p className="text-small text-primary-foreground/80">
              En demonstration av ytorna: bilder från vardagen, hälsning från rektor, personal, öppet hus och lediga tjänster — allt ifyllt av skolan själv.
            </p>
            <span className="mt-1 inline-flex items-center gap-1.5 text-small font-semibold text-accent">
              Se hur en färdig profil ser ut <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden />
            </span>
          </Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
          <StatCard icon={HandHeart} value="Gratis" label="Grundprofilen kostar ingenting" />
          {splitEnheter ? (
            <StatCard icon={Database} value={splitEnheter} label="Skolor i registret redan i dag" />
          ) : null}
          {splitKommuner ? (
            <StatCard icon={Users} value={splitKommuner} label="Kommuner där familjer söker" />
          ) : null}
        </div>
      </div>

    </GronYta>
  );
}

function StatCard({ icon: Icon, value, label }: { icon: React.ComponentType<{ className?: string }>; value: string; label: string }) {
  return (
    <div className="group rounded-card border border-primary-foreground/15 bg-primary-hover/50 p-card transition hover:-translate-y-1 hover:border-green-700 hover:bg-primary-hover/70">
      <span className="grid h-10 w-10 place-items-center rounded-card bg-green-100 text-green-700 transition group-hover:bg-green-700 group-hover:text-ink">
        <Icon className="h-5 w-5" />
      </span>
      <p className="mono mt-6 whitespace-nowrap text-h3 text-primary-foreground xl:text-h1">{value}</p>
      <p className="mt-1 break-words text-small text-primary-foreground/80">{label}</p>
    </div>
  );
}

function FinalCTA() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-primary-soft/40 via-background to-accent-soft/30 py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-5 text-center lg:px-8">
        <p className="eyebrow inline-flex items-center gap-2 justify-center">
          <Sparkles className="h-3.5 w-3.5 text-primary" /> Redo att börja?
        </p>
        <h2 className="mx-auto mt-4 max-w-3xl font-display text-h1 leading-tight text-ink md:text-display">
          Ta första steget —{" "}
          <span className="underline-accent">helt utan konto</span>.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-h4 text-ink-muted">
          Spara favoriter, jämför sida vid sida och skicka intresseanmälningar direkt från profilen.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/hitta-skola"
            className="inline-flex items-center gap-2 rounded-card bg-primary px-7 py-4 text-body font-semibold text-primary-foreground shadow-1 transition hover:bg-primary-hover"
          >
            Hitta rätt skola <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/skolor"
            className="inline-flex items-center gap-2 rounded-card border border-border bg-surface px-7 py-4 text-body font-semibold text-ink transition hover:bg-primary-soft/40"
          >
            Bläddra bland alla skolor
          </Link>
        </div>
      </div>
    </section>
  );
}

function GuidesStrip() {
  return (
    <Section>
      <div className="max-w-2xl">
        <p className="eyebrow inline-flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-primary" /> Guider och verktyg
        </p>
        <h2 className="mt-3 font-display text-h1 md:text-h1">
          Allt du behöver inför <span className="underline-accent">skolvalet</span>
        </h2>
        <p className="mt-3 text-ink-muted">
          Fördjupningar, räknare och matchning — skrivna för familjer, elever och studie- och yrkesvägledare.
        </p>
      </div>
      <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {guideLinks.map((g) => (
          <li key={g.to}>
            <Link
              to={g.to}
              className="group flex h-full flex-col rounded-card border border-border bg-surface p-card transition hover:-translate-y-1 hover:border-primary/30 hover:shadow-1"
            >
              <h3 className="font-display text-h3 leading-snug text-ink group-hover:text-primary">{g.title}</h3>
              <p className="mt-2 flex-1 text-small leading-relaxed text-ink-muted">{g.text}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-small font-semibold text-primary">
                {g.cta} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function HomeFaq() {
  return (
    <Section>
      <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        <div className="lg:sticky lg:top-24 lg:h-fit">
          <p className="eyebrow">Vanliga frågor</p>
          <h2 className="mt-3 font-display text-h1 md:text-h1">Det familjer brukar undra över</h2>
          <p className="mt-4 text-ink-muted">
            Hittar du inte svaret? Läs mer om hur vi arbetar med data och verifiering.
          </p>
          <Link
            to="/om"
            className="mt-5 inline-flex items-center gap-1.5 text-small font-semibold text-primary hover:underline"
          >
            Om Skolvyn <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="divide-y divide-border rounded-card border border-border bg-surface px-6">
          {HOME_FAQ.map((item) => (
            <details key={item.q} className="group py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-h4 text-ink marker:hidden">
                <span>{item.q}</span>
                <span
                  aria-hidden="true"
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-pill bg-primary-soft text-primary transition group-open:rotate-45"
                >
                  +
                </span>
              </summary>

              <p className="mt-3 text-small leading-relaxed text-ink-muted">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </Section>
  );
}

